import React, { useState, useEffect, useRef } from "react";
import { AiOutlineCopy, AiOutlineGlobal, AiOutlineWifi, AiOutlineDashboard, AiOutlineStop } from "react-icons/ai";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import "./NetworkInfoPage.css";
import iranFlag from "./assets/iran-ls.png";

const PING_SERVERS = [
  { name: "Google", url: "https://www.google.com/favicon.ico" },
  { name: "Cloudflare", url: "https://www.cloudflare.com/favicon.ico" },
  { name: "Vercel", url: "https://vercel.com/favicon.ico" }
];

const SPEED_SIZES = [
  { label: "1 MB", bytes: 1 * 1024 * 1024 },
  { label: "5 MB", bytes: 5 * 1024 * 1024 },
  { label: "10 MB", bytes: 10 * 1024 * 1024 },
  { label: "25 MB", bytes: 25 * 1024 * 1024 }
];

const NetworkInfoPage = () => {
  const [ipData, setIpData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [ping, setPing] = useState(null);
  const [testingPing, setTestingPing] = useState(false);
  const [pingHistory, setPingHistory] = useState([]);
  const [pingServer, setPingServer] = useState(PING_SERVERS[1]);
  
  const [speed, setSpeed] = useState(null);
  const [testingSpeed, setTestingSpeed] = useState(false);
  const [speedHistory, setSpeedHistory] = useState([]);
  const [speedSize, setSpeedSize] = useState(SPEED_SIZES[2]); // Default 10MB
  
  const [error, setError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const stopRefs = useRef({ ping: false, speed: false });
  const receivedBytesRef = useRef(0);

  useEffect(() => {
    fetchIpData();
    testPing();
    testSpeed();
  }, []);

  const fetchIpData = async () => {
    const primaryUrl = "https://ipapi.co/json/";
    const secondaryUrl = "https://get.geojs.io/v1/ip/geo.json";
    const timeoutMsg = "Primary API timed out after 5s";

    try {
      setLoading(true);
      setError(null);

      // Helper for fetch with timeout
      const fetchWithTimeout = async (url, ms) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), ms);
        try {
          const response = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (!response.ok) throw new Error(`Fetch failed: ${response.statusText}`);
          return await response.json();
        } catch (e) {
          clearTimeout(timeoutId);
          if (e.name === 'AbortError') throw new Error(timeoutMsg);
          throw e;
        }
      };

      try {
        // Try primary with 5s timeout
        const data = await fetchWithTimeout(primaryUrl, 5000);
        setIpData(data);
      } catch (e) {
        console.warn(`${e.message}, trying fallback...`);
        // Fallback to geojs.io (no explicit timeout here to allow it to finish, or use a longer one)
        const fallbackData = await fetch(secondaryUrl).then(res => res.json());
        
        // Normalize geojs.io data
        setIpData({
          ip: fallbackData.ip,
          country_code: fallbackData.country_code,
          country_name: fallbackData.country,
          city: fallbackData.city,
          org: fallbackData.organization_name || fallbackData.organization,
          asn: fallbackData.asn || "N/A"
        });
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching IP data:", err);
      setError("Failed to fetch IP information from all sources.");
      setLoading(false);
    }
  };

  const handleCopyIP = () => {
    if (ipData?.ip) {
      navigator.clipboard.writeText(ipData.ip);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    }
  };

  const formatIP = (ip) => {
    if (!ip) return "0.0.0.0";
    if (ip.includes(":")) {
      // Truncate IPv6
      const parts = ip.split(":");
      return parts[0] + "::";
    }
    return ip;
  };

  const testPing = async () => {
    setTestingPing(true);
    setPing(null);
    setPingHistory([]);
    setError(null);
    stopRefs.current.ping = false;

    let count = 0;
    while (!stopRefs.current.ping && count < 20) {
      const start = performance.now();
      try {
        await fetch(pingServer.url, { mode: 'no-cors', cache: 'no-cache' });
        const end = performance.now();
        const duration = Math.round(end - start);
        setPing(duration);
        setPingHistory(prev => [...prev, { time: count++, value: duration }].slice(-50));
      } catch (err) {
        console.error("Ping test failed:", err);
        setPing("Error");
        setError(`Ping to ${pingServer.name} failed.`);
        break;
      }
      if (!stopRefs.current.ping) {
        await new Promise(r => setTimeout(r, 100)); // gap
      }
    }
    setTestingPing(false);
  };

  const testSpeed = async () => {
    setTestingSpeed(true);
    setSpeed(null);
    setSpeedHistory([]);
    setError(null);
    stopRefs.current.speed = false;
    receivedBytesRef.current = 0;

    const url = `https://speed.cloudflare.com/__down?bytes=${speedSize.bytes}`;
    const startTime = performance.now();
    let lastTime = startTime;
    let lastBytes = 0;
    let tick = 0;

    const interval = setInterval(() => {
      const now = performance.now();
      const elapsed = (now - lastTime) / 1000;
      const bytesNow = receivedBytesRef.current;
      const bytesDiff = bytesNow - lastBytes;
      const currentSpeedMbps = ((bytesDiff * 8) / elapsed / 1024 / 1024).toFixed(2);
      
      setSpeedHistory(prev => [...prev, { time: tick++, value: parseFloat(currentSpeedMbps) }].slice(-50));
      
      lastTime = now;
      lastBytes = bytesNow;

      if (stopRefs.current.speed) clearInterval(interval);
    }, 100);

    try {
      const response = await fetch(url + "&n=" + Math.random(), { cache: 'no-cache' });
      const reader = response.body.getReader();
      
      while(true) {
        if (stopRefs.current.speed) break;
        const {done, value} = await reader.read();
        if (done) break;
        receivedBytesRef.current += value.length;
      }
      
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      const finalSpeedMbps = ((receivedBytesRef.current * 8) / duration / 1024 / 1024).toFixed(2);
      setSpeed(finalSpeedMbps);
    } catch (err) {
      console.error("Speed test failed:", err);
      setSpeed("Error");
      setError("Speed test failed.");
    } finally {
      clearInterval(interval);
      setTestingSpeed(false);
    }
  };

  const stopPing = () => { stopRefs.current.ping = true; };
  const stopSpeed = () => { stopRefs.current.speed = true; };

  const Chart = ({ data, color, dataKey }) => (
    <div className="chart-wrapper">
      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#111" vertical={false} />
          <XAxis dataKey="time" hide />
          <YAxis hide domain={['auto', 'auto']} padding={{ top: 10, bottom: 10 }} />
          <Tooltip 
            isAnimationActive={false}
            contentStyle={{ backgroundColor: '#000', border: '1px solid #222', fontSize: '10px' }}
            itemStyle={{ color: '#fff' }}
            labelStyle={{ display: 'none' }}
          />
          <Line 
            isAnimationActive={false}
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={2} 
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  if (loading) {
    return (
      <div className="network-info-container">
        <div className="loading-spinner">Detecting Connection...</div>
      </div>
    );
  }

  return (
    <div className="network-info-container dashboard-style">
      <div className="monitor-header">
        <h1>Network Tests</h1>
      </div>

      <div className="metrics-grid">
        <div className="metric-card ip-card">
          <div className="metric-header">
            <h3>Client IP</h3>
            <button className="copy-btn" onClick={handleCopyIP} title="Copy IP">
              <AiOutlineCopy color={copyFeedback ? "#4ade80" : "inherit"} />
            </button>
          </div>
          <div className="metric-value ip-value">{formatIP(ipData?.ip)}</div>
          <div className="metric-footer">
            {ipData?.country_code && (
              <img 
                src={ipData.country_code.toUpperCase() === "IR" ? iranFlag : `https://flagcdn.com/w40/${ipData.country_code.toLowerCase()}.png`} 
                alt={ipData.country_name} 
                className="flag-img"
              />
            )}
            <span>{ipData?.city}, {ipData?.country_name}</span>
          </div>
        </div>

        <div className="metric-card isp-card">
          <h3>ISP / Network</h3>
          <div className="metric-value small-value">{ipData?.org || "Unknown ISP"}</div>
          <div className="metric-footer">
            <AiOutlineGlobal /> {ipData?.asn || "N/A"}
          </div>
        </div>
      </div>

      <div className="test-section">
        <div className="input-grid">
          <div className="metric-card test-card">
            <div className="test-header">
              <div className="test-title">
                <AiOutlineWifi /> PING
              </div>
              <div className="test-result">
                {testingPing ? "..." : ping ? `${ping}ms` : "--"}
              </div>
            </div>
            
            <Chart data={pingHistory} color="#fff" />

            <div className="input-group">
              <label>Target Server</label>
              <select 
                value={pingServer.name} 
                onChange={(e) => setPingServer(PING_SERVERS.find(s => s.name === e.target.value))}
                disabled={testingPing}
              >
                {PING_SERVERS.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>

            <div className="btn-group-row">
              <button className="start-button" onClick={testPing} disabled={testingPing}>
                {testingPing ? "Testing..." : "Test Latency"}
              </button>
              {testingPing && (
                <button className="stop-action-btn" onClick={stopPing} title="Stop">
                  <AiOutlineStop />
                </button>
              )}
            </div>
          </div>

          <div className="metric-card test-card">
            <div className="test-header">
              <div className="test-title">
                <AiOutlineDashboard /> SPEED
              </div>
              <div className="test-result">
                {testingSpeed ? "..." : speed ? `${speed}Mbps` : "--"}
              </div>
            </div>

            <Chart data={speedHistory} color="#fff" />

            <div className="input-group">
              <label>Download Size</label>
              <select 
                value={speedSize.label} 
                onChange={(e) => setSpeedSize(SPEED_SIZES.find(s => s.label === e.target.value))}
                disabled={testingSpeed}
              >
                {SPEED_SIZES.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
              </select>
            </div>

            <div className="btn-group-row">
              <button className="start-button" onClick={testSpeed} disabled={testingSpeed}>
                {testingSpeed ? "Running..." : "Run Speed Test"}
              </button>
              {testingSpeed && (
                <button className="stop-action-btn" onClick={stopSpeed} title="Stop">
                  <AiOutlineStop />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="network-error">
           {error}
        </div>
      )}
    </div>
  );
};

export default NetworkInfoPage;
